<template>
    <div class="container">
        <div class="row my-4 align-items-center">
            <div class="col-2">
                <button class="button-back mx-3" @click="back()">
                    <img src="../assets/back-button.png" alt="Button Image">
                </button>
            </div>
            <div class="col-8 text-center">
                <h2 class="style-font-bold">Prontuário</h2>
            </div>
        </div>
        <div class="row mb-3">
            <div class="offset-4 col-4">
                <div class="patient-header">
                    <div class="display-4 text-primary">
                        <i class="bi bi-person-circle"></i>
                    </div>
                    <div class="flex-1">
                        <div>Paciente: <strong class="fw-semibold">{{ patient.name }}</strong></div>
                        <div v-if="patient.document_type">{{ patient.document_type }}: <strong class="fw-bold">{{ patient.document }}</strong></div>
                        <div>Data de Nascimento: <strong class="fw-bold">{{ patient.birthdate }} ({{ calculateAge(patient.birthdate) }} Anos)</strong></div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="isLoadingInitial" class="text-center mt-3">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Carregando...</span>
            </div>
            <p class="mt-4">Carregando...</p>
        </div>

        <div v-else>
            <div class="container">
                <div class="offset-4 col-4">
                    <div class="filter-chips">
                        <label
                            v-for="specialtyType in uniqueSpecialtyTypes"
                            :key="specialtyType.id"
                            class="filter-chip"
                            :class="{ 'active': activeFilters.includes(specialtyType.id) }"
                        >
                            <input
                                type="checkbox"
                                :value="specialtyType.id"
                                v-model="activeFilters"
                                class="filter-checkbox"
                            />
                            {{ specialtyType.name }}
                        </label>
                    </div>
                </div>
                <div class="offset-4 col-4 mt-2">
                    <div v-if="isLoadingCases" class="text-center mt-3">
                        <div class="spinner-border" role="status">
                            <span class="visually-hidden">Carregando casos...</span>
                        </div>
                        <p class="mt-4">Carregando casos...</p>
                    </div>
                    <div v-else>
                        <div v-for="(protocol, index) in filteredCases" :key="protocol.id" class="protocol-item mt-3">
                            <div class="protocol-header" @click="toggleProtocol(protocol.id)">
                                <div class="protocol-icon">
                                    <img
                                        v-if="protocol.specialty_info && protocol.specialty_info.icon_url"
                                        :src="protocol.specialty_info.icon_url"
                                        :alt="protocol.specialty_type || 'Especialidade'"
                                        class="protocol-icon-img"
                                    />
                                    <i v-else class="bi bi-file-medical fs-4 text-primary"></i>
                                </div>
                                <div class="protocol-header-info flex-1">
                                    <p class="protocol-title">{{ protocol.specialty_type }}: {{ protocol.specialty }}</p>
                                    <p class="protocol-subtitle">Instituição: {{ protocol.company_info && protocol.company_info.name || 'Não informada' }}</p>
                                    <p class="protocol-subtitle">{{ protocol.created_at }}</p>
                                </div>
                                <i :class="['bi', expandedProtocols[protocol.id] ? 'bi-chevron-up' : 'bi-chevron-down', 'fs-5', 'text-secondary']"></i>
                            </div>

                            <div v-if="expandedProtocols[protocol.id]" class="protocol-updates">
                                <div v-if="protocol.updates && protocol.updates.length > 0">
                                    <div
                                        v-for="(update, uIndex) in protocol.updates"
                                        :key="update.id"
                                        class="update-item"
                                    >
                                        <div class="update-line-wrapper">
                                            <div class="update-dot"></div>
                                            <div v-if="uIndex < protocol.updates.length - 1" class="update-line"></div>
                                        </div>
                                        <div class="update-content">
                                            <p class="update-date">{{ update.created_at }}</p>
                                            <p class="update-info">Especialista: {{ update.responsible_info && update.responsible_info.name || 'Não informado' }}</p>
                                            <div class="mt-2 mb-3">
                                                <button
                                                    v-if="update.report_id"
                                                    class="btn btn-primary btn-sm"
                                                    @click="showReport(update.report_id)"
                                                >
                                                    Ver Caso
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p v-else class="text-muted ms-3">Nenhuma atualização encontrada.</p>
                            </div>

                            <div v-if="index < filteredCases.length - 1" class="protocol-separator"></div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";

export default {
    name: "MedicalRecordDetail",
    computed: {
        ...mapGetters({User: "StateUser"}),
        uniqueSpecialtyTypes() {
            if (!this.patientCases || this.patientCases.length === 0) return [];
            const typesMap = new Map();
            this.patientCases.forEach((c) => {
                const specType = c.specialty_type;
                if (specType) {
                    if (!typesMap.has(specType)) {
                        typesMap.set(specType, {
                            id: specType,
                            name: specType
                        });
                    }
                }
            });
            return Array.from(typesMap.values());
        },
        filteredCases() {
            if (this.activeFilters.length === 0) {
                return this.patientCases;
            }
            return this.patientCases.filter((c) => this.activeFilters.includes(c.specialty_type));
        },

    },
    data() {
        return {
            allSpecialties: [],
            activeFilters: [],
            patientCases: [],
            expandedProtocols: {},
            isLoadingInitial: true,
            isLoadingCases: false,
        };
    },
    methods: {
        ...mapGetters(["isAuthenticated"]),
        ...mapActions(["GetPatientMedicalRecord", "GetReportById"]),
        calculateAge(birthdate) {
            if (!birthdate) return "";
            const [day, month, year] = birthdate.split("/").map(Number);
            const today = new Date();
            const birth = new Date(year, month - 1, day);
            let age = today.getFullYear() - birth.getFullYear();
            const hasHadBirthdayThisYear =
                today.getMonth() > birth.getMonth() ||
                (today.getMonth() === birth.getMonth() &&
                    today.getDate() >= birth.getDate());
            if (!hasHadBirthdayThisYear) age--;
            return age;
        },
        back() {
            this.$router.push({ name: 'MedicalRecord' });
        },
        toggleProtocol(protocolId) {
            this.expandedProtocols = {
                ...this.expandedProtocols,
                [protocolId]: !this.expandedProtocols[protocolId]
            };
        },
        async fetchPatientCases() {
            if (!this.patient || !this.patient.id) return;
            this.isLoadingCases = true;
            try {
                let result = await this.GetPatientMedicalRecord({
                    patient_id: this.patient.id
                });
                if (result && result.data && result.data.protocols_info) {
                    this.patientCases = result.data.protocols_info;
                } else {
                    this.patientCases = [];
                }
            } catch (error) {
                console.error("Erro ao buscar os casos do paciente:", error);
                this.patientCases = [];
            } finally {
                this.isLoadingCases = false;
            }
        },
        async showReport(reportId) {
            await this.GetReportById(reportId);
            this.$router.push({ name: 'HistoricalReview' });
        },
    },
    async created() {
        if (!this.isAuthenticated()) {
            this.$router.push("/login");
            return;
        }

        if (history.state?.patient) {
            this.patient = {...this.patient, ...JSON.parse(history.state.patient)};
        } else {
            const stored = localStorage.getItem('patient_info');
            if (stored) {
                this.patient = {...this.patient, ...JSON.parse(stored)};
                localStorage.removeItem('patient_info');
            }
        }

        try {
            this.isLoadingInitial = true;
            await this.fetchPatientCases();
        } catch (error) {
            console.error("Erro ao carregar os dados:", error);
        } finally {
            this.isLoadingInitial = false;
        }
    },
};
</script>

<style scoped>
.button-back {
    background-color: #ffff;
    border: none;
}

.patient-header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
}

.filter-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 4px;
}

.filter-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1.5px solid #cccccc;
    border-radius: 20px;
    padding: 5px 14px;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: #777777;
    cursor: pointer;
    user-select: none;
    transition: border-color 0.2s, color 0.2s;
}

.filter-chip.active {
    border-color: #6200ee;
    color: #6200ee;
}

.filter-checkbox {
    accent-color: #6200ee;
    width: 14px;
    height: 14px;
}

.protocol-item {
    border: 1px solid #e4e4e4;
    border-radius: 8px;
    overflow: hidden;
}

.protocol-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    cursor: pointer;
    background-color: #fafafa;
    user-select: none;
}

.protocol-header:hover {
    background-color: #f0f0f0;
}

.protocol-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.protocol-icon-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 50%;
}

.protocol-header-info {
    flex: 1;
}

.protocol-title {
    font-family: "Roboto", sans-serif;
    font-weight: bold;
    font-size: 13px;
    margin: 0 0 2px 0;
}

.protocol-subtitle {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: #777777;
    margin: 0;
}

.protocol-updates {
    padding: 8px 14px 4px 14px;
    border-top: 1px solid #e4e4e4;
}

.update-item {
    display: flex;
    gap: 10px;
}

.update-line-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    padding-top: 4px;
}

.update-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #6200ee;
    flex-shrink: 0;
}

.update-line {
    width: 2px;
    flex: 1;
    background-color: #e4e4e4;
    min-height: 16px;
}

.update-content {
    flex: 1;
    padding-bottom: 6px;
}

.update-date {
    font-family: "Roboto", sans-serif;
    font-weight: bold;
    font-size: 13px;
    margin: 0 0 2px 0;
}

.update-info {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: #777777;
    margin: 1px 0;
}

.protocol-separator {
    height: 10px;
}
</style>
